import { useId, useState } from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    TextField,
} from '@mui/material';

import { Icon } from '../shared/Icon';
import { QAWebToolButton } from '../shared/QAWebToolButton';
import { ColorCircleComponent } from './templates/inputs/ColorInput';
import { GradientInput } from './templates/inputs/GradientInput';
import { MediaInput } from './templates/inputs/MediaInput';
import { Loading } from '../shared';
import {
    PortalSettingsGradientDirection,
    PortalSettingsTilingType,
    SETTINGS_GRADIENT_DIRECTION,
    SETTINGS_TILING_TYPE,
} from '@lifeserver/shared';

import {
    PortalSettingsDTO,
    UpsertSettingsBodyDTO,
    usePortalSetting,
    usePortalSettings,
} from '../hooks/portal';
import { usePreviewPortalPayload } from '../hooks/usePreviewPortalPayload';

import styles from './PortalSettings.module.scss';

interface PortalSettingsProps {
    settingId?: string;
    onBack: () => void;
}

export function PortalSettings({ settingId, onBack }: PortalSettingsProps) {
    const { data: setting } = usePortalSetting(settingId);
    const { upsertSetting, deleteSetting } = usePortalSettings();

    if (settingId && !setting) {
        return <Loading />;
    }

    return (
        <PortalSettingsEditor
            settingId={settingId ?? crypto.randomUUID()}
            setting={setting}
            upsertSetting={upsertSetting}
            deleteSetting={deleteSetting}
            onBack={onBack}
        />
    );
}

interface PortalSettingsEditorProps {
    settingId: string;
    setting?: PortalSettingsDTO;
    upsertSetting: ReturnType<typeof usePortalSettings>['upsertSetting'];
    deleteSetting: ReturnType<typeof usePortalSettings>['deleteSetting'];
    onBack: () => void;
}

function PortalSettingsEditor({
    settingId,
    setting,
    upsertSetting,
    deleteSetting,
    onBack,
}: PortalSettingsEditorProps) {
    const backgroundColorId = useId();
    const categoryBarId = useId();

    const [currentSetting, setCurrentSetting] = useState<PortalSettingsDTO>({
        id: settingId,
        name: setting?.name ?? '',
        tilingAsset: setting?.tilingAsset ?? '',
        tilingAssetOpacity: setting?.tilingAssetOpacity ?? 100,
        tilingType: setting?.tilingType ?? 'bricks',
        backgroundColors: setting?.backgroundColors ?? [
            { r: 1, g: 1, b: 1, a: 1 },
        ],
        headerColor: setting?.headerColor ?? { r: 1, g: 1, b: 1, a: 1 },
        gradientDirection: setting?.gradientDirection ?? null,
        active: setting?.active ?? false,
        createdAt: new Date().toISOString(),
    });

    const useGradient = currentSetting.backgroundColors.length > 1;

    usePreviewPortalPayload([], { leading: true }, currentSetting);

    const handleSave = async () => {
        if (!currentSetting.name) {
            alert('Please fill in a name');
            return;
        }

        const dto: UpsertSettingsBodyDTO = {
            name: currentSetting.name,
            tilingAsset: currentSetting.tilingAsset,
            tilingAssetOpacity: currentSetting.tilingAssetOpacity,
            tilingType: currentSetting.tilingType,
            headerColor: currentSetting.headerColor,
            backgroundColors: currentSetting.backgroundColors,
            gradientDirection: useGradient
                ? currentSetting.gradientDirection
                : null,
        };

        try {
            await upsertSetting.mutateAsync({
                id: settingId,
                upsertSettingsBodyDTO: dto,
            });
            onBack();
        } catch (error) {
            console.error('Failed to save setting:', error);
            alert('Failed to save setting');
        }
    };

    const handleDelete = async () => {
        if (!settingId) return;
        if (!confirm('Are you sure you want to delete this setting?')) return;

        try {
            await deleteSetting.mutateAsync({ id: settingId });
            onBack();
        } catch (error) {
            console.error('Failed to delete setting:', error);
            alert('Failed to delete setting');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Button
                    variant="text"
                    onClick={onBack}
                    className={styles.backButton}
                >
                    ← Back
                </Button>
                <h2>{settingId ? 'Edit Setting' : 'Create New Setting'}</h2>
                {settingId && (
                    <Button
                        aria-label="Delete"
                        color="error"
                        variant="outlined"
                        onClick={handleDelete}
                        endIcon={<Icon name="delete" />}
                        sx={{ height: 56 }}
                        disabled={currentSetting.active}
                    >
                        Delete
                    </Button>
                )}
            </div>

            <div className={styles.form}>
                <div className={styles.settingGroup}>
                    <TextField
                        label="Name"
                        value={currentSetting.name}
                        onChange={(e) =>
                            setCurrentSetting({
                                ...currentSetting,
                                name: e.target.value,
                            })
                        }
                        placeholder="Setting Name"
                        fullWidth
                        variant="outlined"
                    />
                </div>

                <div className={styles.settingGroup}>
                    <div className={styles.settingHeader}>
                        <label className={styles.label}>Tiling Asset</label>
                    </div>
                    <MediaInput
                        value={
                            currentSetting.tilingAsset
                                ? {
                                      url: currentSetting.tilingAsset,
                                      mediaType: 'Image',
                                  }
                                : undefined
                        }
                        onChange={(media) =>
                            setCurrentSetting({
                                ...currentSetting,
                                tilingAsset: media?.url ?? '',
                            })
                        }
                    />
                    <div className={styles.alphaSliderContainer}>
                        <label className={styles.alphaLabel}>Opacity</label>
                        <Slider
                            value={currentSetting.tilingAssetOpacity}
                            onChange={(_, value) =>
                                setCurrentSetting({
                                    ...currentSetting,
                                    tilingAssetOpacity: value as number,
                                })
                            }
                            min={0}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value}%`}
                            className={styles.alphaSlider}
                        />
                        <span className={styles.alphaValue}>
                            {currentSetting.tilingAssetOpacity}%
                        </span>
                    </div>
                </div>

                <div className={styles.settingGroup}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Tiling Type</InputLabel>
                        <Select
                            value={currentSetting.tilingType}
                            onChange={(e) =>
                                setCurrentSetting({
                                    ...currentSetting,
                                    tilingType: e.target
                                        .value as PortalSettingsTilingType,
                                })
                            }
                            label="Tiling Type"
                        >
                            {SETTINGS_TILING_TYPE.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type.charAt(0).toUpperCase() +
                                        type.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <div className={styles.settingGroup}>
                    <div className={styles.settingHeader}>
                        <label className={styles.label}>Header</label>
                    </div>
                    <ColorCircleComponent
                        color={currentSetting.headerColor}
                        id={categoryBarId}
                        selectedColor={currentSetting.headerColor}
                        onChange={(color) =>
                            setCurrentSetting({
                                ...currentSetting,
                                headerColor: color,
                            })
                        }
                        custom={true}
                        onColorEdit={(color) =>
                            setCurrentSetting({
                                ...currentSetting,
                                headerColor: color,
                            })
                        }
                        hideEditButton={true}
                    />
                    <div className={styles.alphaSliderContainer}>
                        <label className={styles.alphaLabel}>Opacity</label>
                        <Slider
                            value={currentSetting.headerColor.a}
                            onChange={(_, value) =>
                                setCurrentSetting({
                                    ...currentSetting,
                                    headerColor: {
                                        ...currentSetting.headerColor,
                                        a: value as number,
                                    },
                                })
                            }
                            min={0}
                            max={1}
                            step={0.01}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) =>
                                `${Math.round(value * 100)}%`
                            }
                            className={styles.alphaSlider}
                        />
                        <span className={styles.alphaValue}>
                            {Math.round(currentSetting.headerColor.a * 100)}%
                        </span>
                    </div>
                </div>

                <div className={styles.settingGroup}>
                    <div className={styles.settingHeader}>
                        <label className={styles.label}>Background</label>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useGradient}
                                    onChange={(e) => {
                                        const newUseGradient = e.target.checked;
                                        if (
                                            !newUseGradient &&
                                            currentSetting.backgroundColors
                                                .length > 0
                                        ) {
                                            setCurrentSetting({
                                                ...currentSetting,
                                                backgroundColors: [
                                                    currentSetting
                                                        .backgroundColors[0],
                                                ],
                                                gradientDirection: null,
                                            });
                                        }
                                        if (
                                            newUseGradient &&
                                            currentSetting.backgroundColors
                                                .length < 2
                                        ) {
                                            setCurrentSetting({
                                                ...currentSetting,
                                                backgroundColors: [
                                                    currentSetting
                                                        .backgroundColors[0] || {
                                                        r: 1,
                                                        g: 0,
                                                        b: 0,
                                                        a: 1,
                                                    },
                                                    { r: 0, g: 0, b: 1, a: 1 },
                                                ],
                                                gradientDirection: 'horizontal',
                                            });
                                        }
                                    }}
                                />
                            }
                            label="Use Gradient"
                            className={styles.toggleLabel}
                        />
                    </div>

                    {useGradient ? (
                        <>
                            <GradientInput
                                value={currentSetting.backgroundColors}
                                onChange={(colors) =>
                                    setCurrentSetting({
                                        ...currentSetting,
                                        backgroundColors: colors,
                                    })
                                }
                            />
                            <div className={styles.gradientDirection}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Gradient Direction</InputLabel>
                                    <Select
                                        value={currentSetting.gradientDirection}
                                        onChange={(e) =>
                                            setCurrentSetting({
                                                ...currentSetting,
                                                gradientDirection: e.target
                                                    .value as PortalSettingsGradientDirection,
                                            })
                                        }
                                        label="Gradient Direction"
                                    >
                                        {SETTINGS_GRADIENT_DIRECTION.map(
                                            (direction) => (
                                                <MenuItem
                                                    key={direction}
                                                    value={direction}
                                                >
                                                    {direction
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        direction.slice(1)}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </div>
                        </>
                    ) : (
                        <ColorCircleComponent
                            color={currentSetting.backgroundColors[0]}
                            id={backgroundColorId}
                            selectedColor={currentSetting.backgroundColors[0]}
                            onChange={(color) =>
                                setCurrentSetting({
                                    ...currentSetting,
                                    backgroundColors: [color],
                                })
                            }
                            custom={true}
                            onColorEdit={(color) =>
                                setCurrentSetting({
                                    ...currentSetting,
                                    backgroundColors: [color],
                                })
                            }
                            hideEditButton={true}
                        />
                    )}
                </div>

                <div className={styles.actions}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={upsertSetting.isPending}
                        className={styles.saveButton}
                    >
                        {upsertSetting.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        className={styles.cancelButton}
                    >
                        Cancel
                    </Button>
                    <QAWebToolButton />
                </div>
            </div>
        </div>
    );
}
